import React, { forwardRef, Ref, useMemo } from 'react';

import Flex, { FlexProps } from '@gojob/ui-bits/Flex';
import { Color } from '@gojob/ui-bits/styles/theme';
import useTheme from '@gojob/ui-bits/styles/useTheme';

export interface WaveProps extends Omit<FlexProps<'div'>, 'ref' | 'color'> {
  color?: Color;
  waveColor?: Color;
  children: React.ReactNode;
}

const useStyles = (color: Color = 'pageBackground', accentColor: Color = 'secondary') => {
  const { colors } = useTheme();

  return useMemo(() => {
    return {
      root: {
        position: 'relative',
        flexDirection: 'column',
        backgroundColor: colors[color],
      },
      wrapper: {
        zIndex: 1,
      },
      wave: {
        zIndex: 0,
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: '50%',
        width: '100%',
        color: colors[accentColor],
      },
    } as const;
  }, [colors, accentColor, color]);
};

const Wave = (props: WaveProps, ref: Ref<HTMLDivElement>) => {
  const { color, waveColor, children, ...other } = props;
  const classes = useStyles(color, waveColor);

  return (
    <Flex ref={ref} data-testid="WaveRoot" css={classes.root} width="100%" {...other}>
      <Flex css={classes.wrapper}>{children}</Flex>
      <svg data-testid="Wave" css={classes.wave} viewBox="0 0 4992 4992">
        <path
          d="M0 4273C2812.5 3078.5 2502.17 844.408 4992 0v4992H0v-719z"
          fill="currentColor"
          fillRule="evenodd"
          preserveAspectRatio="none"
        />
      </svg>
    </Flex>
  );
};

export default forwardRef(Wave);
